import React, { useState, useRef } from 'react';
import { Cpu, HardDrive, MemoryStick, ArrowUpDown, Network, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { mainPlans, additionalPlans } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { openBilling } from '../lib/referral';

export const PricingSection = ({ hideHeader = false, title, subtitle }) => {
  const { t, language } = useLanguage();
  const allPlans = [...mainPlans, ...additionalPlans];
  const ruLang = language === 'ru';

  // Default: first plan (Starter, €2.98) — emphasizes entry price
  const [selectedId, setSelectedId] = useState(allPlans[0].id);
  const flagshipRef = useRef(null);
  const selected = allPlans.find((p) => p.id === selectedId) || allPlans[0];

  const goRegister = () => {
    const lang = window.location.pathname.startsWith('/ru') ? 'ru' : 'en';
    openBilling('register', lang);
  };

  const formatPrice = (price) => ({
    whole: Math.floor(price),
    cents: Math.round((price % 1) * 100).toString().padStart(2, '0'),
  });

  return (
    <section
      id={hideHeader ? '' : 'pricing'}
      className={hideHeader ? '' : 'relative py-24 lg:py-32 overflow-hidden'}
    >
      {!hideHeader && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/40 to-white"></div>
        </div>
      )}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {!hideHeader && (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 mb-4 tracking-tight">
              {title || t('pricing.title')}
            </h2>
            <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
              {subtitle || t('pricing.subtitle')}
            </p>
          </div>
        )}

        {/* Flagship featured card — shows currently selected plan */}
        <div ref={flagshipRef} className="relative mb-8">
          <div className="absolute -inset-6 bg-gradient-to-r from-blue-500/30 via-blue-400/30 to-blue-500/30 rounded-[2rem] blur-3xl opacity-60 pointer-events-none"></div>

          <div className="relative rounded-3xl p-8 md:p-10 overflow-hidden transition-all duration-500" style={{ background: 'linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 55%,#2563eb 100%)' }}>
            <div
              className="absolute inset-0 opacity-10 pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)',
                backgroundSize: '24px 24px',
              }}
            ></div>
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative grid lg:grid-cols-[1fr_auto_1fr] gap-8 items-center">
              {/* Left: Badge + name + price */}
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-blue-300 text-[11px] font-semibold uppercase tracking-widest mb-5">
                  <Sparkles className="w-3 h-3" />
                  {selected.popular
                    ? (ruLang ? 'Рекомендуем' : 'Recommended')
                    : (ruLang ? 'Выбранный тариф' : 'Selected plan')}
                </div>
                <div className="text-blue-300 text-sm font-medium mb-2">
                  {selected.name}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl text-blue-300">€</span>
                  <span className="text-7xl md:text-8xl font-bold tracking-tight text-white leading-none">
                    {formatPrice(selected.price).whole}
                  </span>
                  <span className="text-2xl text-blue-300">
                    .{formatPrice(selected.price).cents}
                  </span>
                </div>
                <div className="text-blue-300 text-sm mt-2">
                  {ruLang ? 'в месяц' : 'per month'}
                </div>
              </div>

              {/* Middle: Specs */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 lg:border-x lg:border-white/10 lg:px-10">
                {[
                  { icon: Cpu, value: selected.cpu, label: 'CPU' },
                  { icon: MemoryStick, value: selected.ram, label: 'RAM' },
                  { icon: HardDrive, value: selected.storage, label: ruLang ? 'Диск NVMe' : 'NVMe' },
                  { icon: Network, value: selected.port, label: ruLang ? 'Порт' : 'Port' },
                  { icon: ArrowUpDown, value: selected.transfer, label: ruLang ? 'Трафик' : 'Transfer' },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-blue-300" />
                      </div>
                      <div>
                        <div className="text-white font-semibold leading-tight">{s.value}</div>
                        <div className="text-blue-300 text-xs">{s.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right: CTA */}
              <div className="flex flex-col items-center gap-3">
                <Button
                  onClick={goRegister}
                  className="group rounded-full py-7 px-8 bg-white hover:bg-blue-50 text-blue-700 font-semibold text-base shadow-xl shadow-black/20 transition-all hover:-translate-y-0.5"
                >
                  {t('pricing.choose')}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>
                <p className="text-blue-300/70 text-xs text-center">
                  {ruLang ? 'Готов через 60 секунд' : 'Ready in 60 seconds'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* All plans selectable — prominent grid */}
        <div className="pt-4">
          <div className="flex items-center justify-between mb-4 px-1">
            <span className="text-sm font-semibold text-slate-700">
              {ruLang ? 'Все тарифы' : 'All plans'}
            </span>
            <span className="text-xs text-slate-400">
              {ruLang ? 'Нажмите, чтобы выбрать' : 'Click to select'}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {allPlans.map((plan) => {
              const { whole, cents } = formatPrice(plan.price);
              const isSelected = plan.id === selectedId;
              return (
                <button
                  key={plan.id}
                  onClick={() => {
                    setSelectedId(plan.id);
                    requestAnimationFrame(() => {
                      if (!flagshipRef.current) return;
                      const headerHeight = 110;
                      const rect = flagshipRef.current.getBoundingClientRect();
                      const targetY = window.pageYOffset + rect.top - headerHeight;
                      const startY = window.pageYOffset;
                      const distance = targetY - startY;
                      if (Math.abs(distance) < 4) return;

                      // Disable CSS smooth-scroll during our RAF loop to avoid jitter
                      const htmlEl = document.documentElement;
                      const bodyEl = document.body;
                      const prevHtml = htmlEl.style.scrollBehavior;
                      const prevBody = bodyEl.style.scrollBehavior;
                      htmlEl.style.scrollBehavior = "auto";
                      bodyEl.style.scrollBehavior = "auto";

                      const duration = 1100;
                      const startTime = performance.now();
                      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
                      const step = (now) => {
                        const elapsed = now - startTime;
                        const progress = Math.min(elapsed / duration, 1);
                        window.scrollTo(0, Math.round(startY + distance * easeOutCubic(progress)));
                        if (progress < 1) {
                          requestAnimationFrame(step);
                        } else {
                          htmlEl.style.scrollBehavior = prevHtml;
                          bodyEl.style.scrollBehavior = prevBody;
                        }
                      };
                      requestAnimationFrame(step);
                    });
                  }}
                  className={`group relative text-left p-4 rounded-2xl transition-all duration-300 ${
                    isSelected
                      ? 'bg-blue-700 text-white shadow-xl shadow-blue-900/30 -translate-y-1 ring-2 ring-blue-500/40'
                      : 'bg-white border-2 border-slate-200 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-700/15 hover:-translate-y-0.5'
                  }`}
                >
                  {plan.popular && !isSelected && (
                    <span className="absolute -top-2 right-3 inline-flex items-center px-2 py-0.5 bg-blue-700 text-white text-[9px] font-bold uppercase tracking-wider rounded-full shadow">
                      ★
                    </span>
                  )}

                  <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isSelected ? 'text-white/80' : 'text-slate-500'}`}>
                    {plan.name}
                  </div>

                  <div className="flex items-baseline gap-0.5 mb-3">
                    <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>€</span>
                    <span className={`text-2xl font-bold tracking-tight ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {whole}
                    </span>
                    <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                      .{cents}
                    </span>
                  </div>

                  <div className={`text-xs space-y-1 ${isSelected ? 'text-white' : 'text-slate-600'}`}>
                    <div className="flex items-center gap-1.5">
                      <Cpu className="w-3 h-3 opacity-60" />
                      <span className="font-medium">{plan.cpu}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MemoryStick className="w-3 h-3 opacity-60" />
                      <span className="font-medium">{plan.ram}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <HardDrive className="w-3 h-3 opacity-60" />
                      <span className="font-medium">{plan.storage}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
